import React from 'react';
import { Button } from 'react-bootstrap';
import StickyLabel from 'components/Flux/Shared/StickyLabel';
import { UnicenarioSectionRow } from 'utils/hooks/flux/automatic/useUnicenarioData';
import { translateClosestDates } from 'components/Flux/Revision/Table';
import { cssVar } from 'constants/theme';
import './index.scss';

const categoryLabels: Record<string, string> = {
  sistema: 'Sistema',
  subsistemas: 'Subsistemas',
  reservatorios: 'Reservatórios de Energia',
  bacias: 'Bacias Hidrográficas',
  postos: 'Postos Hidroenergéticos',
};

interface UnicenarioTableProps {
  unicenarioSections: UnicenarioSectionRow[];
  colLabels: string[];
  popDataModal: (chosenPeriod: string) => void;
  /** Congela a coluna de rótulo com teto de largura. Só o mobile passa — desktop segue igual. */
  stickyRowLabel?: boolean;
}

const DESTAQUE_REVISAO = '#FF00007F'; // tema: cor de dado

export default function UnicenarioTable({
  unicenarioSections, colLabels, popDataModal, stickyRowLabel = false,
}: UnicenarioTableProps) {
  const cellStyle = (backgroundColor: string = cssVar('bg-surface')): React.CSSProperties => ({
    textDecoration: 'none',
    // Destaque vermelho é cor de dado e leva texto branco; o resto segue o tema.
    color: backgroundColor === DESTAQUE_REVISAO ? 'white' : cssVar('text-strong'),
    backgroundColor,
    padding: '4px 8px',
    display: 'block',
  });

  const tableClass = [
    'table',
    'sticky-table-col-row',
    stickyRowLabel ? 'sticky-table-capped' : '',
    'unicenario-table',
  ].filter(Boolean).join(' ');

  return (
    <div>
      <table className={tableClass}>
        <thead>
          <tr>
            <th style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>Locais</th>
            {colLabels.map((colLabel) => (
              <th scope="col" className="unicenario-col-label" key={colLabel}>
                {translateClosestDates(colLabel)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {unicenarioSections.map((section) => {
            const sectionLabel = categoryLabels[section.category] || section.category;
            return (
              <React.Fragment key={section.category}>
                <tr>
                  <th className="unicenario-section-header">
                    {stickyRowLabel
                      ? <StickyLabel text={sectionLabel} />
                      : sectionLabel}
                  </th>
                  {colLabels.map((col) => (
                    <td
                      key={`${section.category}-sep-${col}`}
                      className="unicenario-section-empty"
                    />
                  ))}
                </tr>
                {section.rows.map((rowData) => (
                  <tr key={`${section.category}-${rowData.label}`}>
                    <th className="unicenario-row-label">
                      {stickyRowLabel
                        ? <StickyLabel text={rowData.label} />
                        : rowData.label}
                    </th>
                    {rowData.values.map(({ value, colorName }, index) => (
                      <td
                        className="unicenario-value-cell"
                        key={`${section.category}-${rowData.label}-${colLabels[index]}`}
                      >
                        <Button
                          variant="link"
                          style={cellStyle(colorName)}
                          onClick={() => popDataModal(colLabels[index])}
                        >
                          {value || ''}
                        </Button>
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
